<script setup>
import { computed, onMounted, ref } from 'vue';
import { useSiteForm } from '../useSiteForm.js';
import { styleVars } from './shared.js';
import EmailTypoHint from '@/Components/EmailTypoHint.vue';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const { values, errors, status, message, submit, turnstileKey, mountTurnstile } = useSiteForm('volunteer', {
    siteData: props.site_data,
    fields: { name: '', email: '', interests: [] },
});

const turnstileEl = ref(null);
onMounted(() => { if (turnstileKey) mountTurnstile(turnstileEl.value); });

const bullets = computed(() => (Array.isArray(props.config.bullets) ? props.config.bullets : [])
    .map((b) => String(b ?? '').trim()).filter(Boolean));
const interests = computed(() => (Array.isArray(props.config.interests) ? props.config.interests : [])
    .map((b) => String(b ?? '').trim()).filter(Boolean));
const successText = computed(() => props.config.success_message || message.value);
</script>

<template>
    <section :id="config.anchor_id || null" class="th-block th-volunteer th-sec" :style="styleVars(config)">
        <div class="th-wrap">
            <div class="th-card th-card--pad th-volunteer__card">
                <div>
                    <p v-if="config.eyebrow" class="th-eyebrow th-volunteer__eyebrow">{{ config.eyebrow }}</p>
                    <h2 v-if="config.heading" class="th-h2 th-h2--md th-volunteer__title">{{ config.heading }}</h2>
                    <p v-if="config.body" class="th-body th-volunteer__body">{{ config.body }}</p>
                    <ul v-if="bullets.length" class="th-bullets">
                        <li v-for="(b, i) in bullets" :key="i">{{ b }}</li>
                    </ul>
                </div>

                <div v-if="status === 'sent'" class="th-form-note th-form-note--ok th-volunteer__sent" role="status">{{ successText }}</div>
                <form v-else class="th-stack th-volunteer__form" novalidate @submit.prevent="submit()">
                    <label class="th-label">Name
                        <input v-model="values.name" class="th-input th-volunteer-name" type="text" name="name" autocomplete="name">
                        <span v-if="errors.name" class="th-field-error">{{ errors.name }}</span>
                    </label>
                    <label class="th-label">Email
                        <input v-model="values.email" class="th-input th-volunteer-email" type="email" name="email" autocomplete="email">
                        <EmailTypoHint :email="values.email" @fix="values.email = $event" />
                        <span v-if="errors.email" class="th-field-error">{{ errors.email }}</span>
                    </label>
                    <template v-if="interests.length">
                        <div class="th-volunteer__label">Interested in</div>
                        <div class="th-chips th-volunteer__interests">
                            <label v-for="opt in interests" :key="opt" class="th-check th-volunteer-interest">
                                <input v-model="values.interests" type="checkbox" :value="opt">{{ opt }}
                            </label>
                        </div>
                        <span v-if="errors.interests" class="th-field-error">{{ errors.interests }}</span>
                    </template>
                    <input v-model="values.website" class="th-hp" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true">
                    <div v-if="turnstileKey" ref="turnstileEl" class="th-volunteer__turnstile"></div>
                    <div v-if="status === 'error' && message" class="th-form-note th-form-note--error" role="alert">{{ message }}</div>
                    <button type="submit" class="th-btn th-btn--primary th-volunteer__submit th-volunteer-submit" :disabled="status === 'sending'">
                        {{ status === 'sending' ? 'Sending…' : (config.button_label || 'Sign me up') }}
                    </button>
                </form>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-volunteer__card {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 360px), 1fr));
    gap: clamp(24px, 4vw, 56px);
    align-items: start;
}
.th-volunteer__eyebrow { margin-bottom: 14px; }
.th-volunteer__title { margin-bottom: 14px; }
.th-volunteer__body { max-width: 44ch; margin-bottom: 20px; }
.th-volunteer__label { font-size: 14px; font-weight: 600; }
.th-volunteer__submit { margin-top: 6px; }
</style>
